// M16 KPI — hücre değeri normalizasyonu (plan Task 3; örnekler gerçek dosya envanterinden,
// finar-yanit-ve-ornek-dosya-analizi.md §B4). Excel/PDF/el girişi hücreleri TR ("1.474.400",
// "4,5") ve EN ("1,234.56") yazımında karışık gelir; tek kaynak burasıdır — BE import
// worker'ı ve FE hücre editörü aynı fonksiyonu kullanır (Davranış kuralı 1).

import { type NumberLocale } from "./kpi-number-locale.js";

// TODO-KPI-37: `ambiguous` — locale "mixed" iken tek gruplu 3 haneli ayraç ("1,234" / "33.745"):
// binlik mi ondalık mı bilinemez; varsayılan okuma yapılır, içe aktarma önizlemesi uyarır.
export type ParsedCell =
  | { kind: "number"; value: number; unit?: string; ambiguous?: true } // "27.405,87 GJ" → 27405.87
  | { kind: "percent"; value: number } // "%20,5" → 20.5 ; "17,88%" → 17.88
  | { kind: "pending" } // "XX" | "XXX" | "xxx" | "X"
  | { kind: "dash" } // "-" | "–" | "—"
  | { kind: "empty" } // "" | null | undefined | whitespace
  | { kind: "text"; raw: string }; // "HENÜZ ÖLÇÜLMEDİ" vb.

/** Birim en fazla bu kadar karakter; aşarsa hücre metin sayılır. */
export const KPI_UNIT_MAX_CHARS = 12;

const PENDING_RE = /^x+$/i;
const DASH_RE = /^[-–—]$/;
// Sondaki dipnot işaretleri ("100%*", "1.250†", "45 **"): rapor tablolarında hücre dipnotu
// işaretidir, değerin parçası değil — soyulur, sayı/yüzde normal çözülür (tur B-06).
const TRAILING_FOOTNOTE_RE = /[\s]*[*†‡]+$/u;
// Sayı gövdesi: rakamla başlar; nokta/virgül ayraçları; boşlukla ayrılmış 3'lü binlik grupları
// (sonrasında rakam/ayraç gelmemeli — "12 3456" sayı değil, "12 adet" ise 12 + birim).
const NUMBER_BODY_RE = /^(\d[\d.,]*(?:\s\d{3}(?![\d.,]))*)/;
// Birim: harfle başlar; harf/rakam/'/'/²/³/nokta/tire/boşluk içerebilir ("ton/yıl", "m³", "tCO2e").
const UNIT_RE = /^\p{L}[\p{L}\p{N}/²³.\-\s]*$/u;

export interface ParseCellOptions {
  /** Sayfa/belge yazım biçimi oyu (`detectCellsNumberLocale`); yoksa bugünkü davranış. */
  locale?: NumberLocale | undefined;
}

interface BodyResult {
  value: number;
  ambiguous: boolean;
}

/** "1,234" / "33.745": tek grup, kesir tam 3 hane, tam kısım 1–3 hane ve "0" değil. */
function isAmbiguousSingleGroup(intPart: string, fracPart: string): boolean {
  return fracPart.length === 3 && intPart.length >= 1 && intPart.length <= 3 && intPart !== "0";
}

/**
 * Yalnız sayı gövdesini (ayraçlı rakam dizisi, boşluksuz) JS sayısına çevirir.
 * Kurallar:
 *  - Hem "." hem "," varsa SONDAKİ ayraç ondalıktır ("27.405,87" TR, "1,234.56" EN).
 *  - Yalnız "," varsa: tek virgül ondalık (TR bağlamı: "4,5"), birden çok virgül binlik (EN).
 *  - Yalnız "." varsa: birden çok nokta binlik ("1.474.400"); tek nokta ve kesir tam 3 hane
 *    ve tam sayı kısmı 1–3 hane (ve "0" değil) → BİNLİK ("33.745" → 33745, "(1.200)" → 1200);
 *    aksi halde ondalık ("33.7", "33.7450", "65.03042882", "1234.567", "0.500").
 *  - Binlik yorumunda her ara grup tam 3 hane olmalı; değilse null (metin).
 *  - TODO-KPI-37 `locale`: "en" tek gruplu virgülü BİNLİK ("1,234" → 1234), tek gruplu noktayı
 *    ONDALIK ("33.745" → 33.745) okur; "tr"/yok bugünkü kural; "mixed" bugünkü kural + belirsiz.
 */
function parseNumberBody(body: string, locale?: NumberLocale): BodyResult | null {
  const s = body.replace(/\s+/g, "");
  if (!/^\d[\d.,]*$/.test(s)) return null;
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  let normalized: string | null = null;
  let ambiguous = false;

  if (lastDot >= 0 && lastComma >= 0) {
    const decimalSep = lastDot > lastComma ? "." : ",";
    const thousandsSep = decimalSep === "." ? "," : ".";
    const [intPart, fracPart, ...rest] = s.split(decimalSep);
    if (rest.length > 0 || intPart === undefined || fracPart === undefined) return null;
    if (fracPart.includes(thousandsSep) || !/^\d+$/.test(fracPart)) return null;
    const intDigits = joinThousandGroups(intPart.split(thousandsSep));
    if (intDigits === null) return null;
    normalized = `${intDigits}.${fracPart}`;
  } else if (lastComma >= 0) {
    const groups = s.split(",");
    if (groups.length === 2) {
      const [intPart = "", fracPart = ""] = groups;
      const single = isAmbiguousSingleGroup(intPart, fracPart);
      normalized = single && locale === "en" ? `${intPart}${fracPart}` : `${intPart}.${fracPart}`;
      ambiguous = single && locale === "mixed";
    } else {
      normalized = joinThousandGroups(groups);
    }
  } else if (lastDot >= 0) {
    const groups = s.split(".");
    if (groups.length === 2) {
      const [intPart = "", fracPart = ""] = groups;
      const single = isAmbiguousSingleGroup(intPart, fracPart);
      const thousands = single && locale !== "en";
      normalized = thousands ? `${intPart}${fracPart}` : `${intPart}.${fracPart}`;
      ambiguous = single && locale === "mixed";
    } else {
      normalized = joinThousandGroups(groups);
    }
  } else {
    normalized = s;
  }

  if (normalized === null || !/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? { value: n, ambiguous } : null;
}

/** Binlik grupları: ilk grup 1–3 hane, diğerleri tam 3 hane; aksi halde null. */
function joinThousandGroups(groups: string[]): string | null {
  const [first, ...rest] = groups;
  if (first === undefined || !/^\d{1,3}$/.test(first)) return null;
  if (!rest.every((g) => /^\d{3}$/.test(g))) return null;
  return first + rest.join("");
}

/**
 * İşaret + parantez + sayı gövdesi + kalan metni ayırır.
 * "(1.200) TL" → { value: -1200, rest: "TL" } · "−12" → { value: -12, rest: "" }.
 */
function parseSignedNumber(
  input: string,
  locale?: NumberLocale,
): { value: number; rest: string; ambiguous: boolean } | null {
  let s = input.trim();
  let negative = false;
  // Parantez negatif: "(1.200)" ya da "(1.200) TL" — kapanıştan sonrası birim/kalan metin.
  const paren = /^\(\s*([^()]+?)\s*\)(.*)$/.exec(s);
  if (paren?.[1] !== undefined) {
    const inner = parseSignedNumber(paren[1], locale);
    if (inner?.rest !== "") return null;
    return { value: -inner.value, rest: (paren[2] ?? "").trim(), ambiguous: inner.ambiguous };
  }
  const signMatch = /^([-+−])\s*/.exec(s);
  if (signMatch) {
    if (signMatch[1] !== "+") negative = !negative;
    s = s.slice(signMatch[0].length);
  }
  const bodyMatch = NUMBER_BODY_RE.exec(s);
  if (!bodyMatch?.[1]) return null;
  const body = bodyMatch[1];
  const parsed = parseNumberBody(body, locale);
  if (parsed === null) return null;
  const rest = s.slice(body.length).trim();
  const value = negative ? -parsed.value : parsed.value;
  return { value, rest, ambiguous: parsed.ambiguous };
}

/** Nesne/bigint/sembol gibi beklenmedik hücre tipleri için güvenli metin ("[object Object]" değil). */
function stringifyUnknown(raw: unknown): string {
  if (typeof raw === "bigint") return raw.toString();
  if (typeof raw === "symbol" || typeof raw === "function") return "";
  try {
    return JSON.stringify(raw) ?? "";
  } catch {
    return "";
  }
}

export function parseCell(raw: unknown, opts: ParseCellOptions = {}): ParsedCell {
  const { locale } = opts;
  if (raw === null || raw === undefined) return { kind: "empty" };
  if (typeof raw === "number") {
    return Number.isFinite(raw)
      ? { kind: "number", value: raw }
      : { kind: "text", raw: String(raw) };
  }
  if (typeof raw === "boolean") return { kind: "text", raw: String(raw) };
  if (raw instanceof Date) {
    return { kind: "text", raw: Number.isNaN(raw.getTime()) ? "Invalid Date" : raw.toISOString() };
  }
  const text = typeof raw === "string" ? raw : stringifyUnknown(raw);
  const original = text.trim();
  if (original === "") return { kind: "empty" };
  // Dipnot işareti soyulunca boş kalıyorsa ("*", "†") metin olarak kalır.
  const stripped = original.replace(TRAILING_FOOTNOTE_RE, "").trim();
  const trimmed = stripped === "" ? original : stripped;
  if (PENDING_RE.test(trimmed)) return { kind: "pending" };
  if (DASH_RE.test(trimmed)) return { kind: "dash" };

  // Yüzde: önde ("%20,5", "% 41,3") ya da sonda ("17,88 %", "0,02%").
  if (trimmed.startsWith("%") || trimmed.endsWith("%")) {
    const inner = trimmed.startsWith("%") ? trimmed.slice(1) : trimmed.slice(0, -1);
    const parsed = parseSignedNumber(inner, locale);
    if (parsed?.rest === "") return { kind: "percent", value: parsed.value };
    return { kind: "text", raw: original };
  }

  const parsed = parseSignedNumber(trimmed, locale);
  if (!parsed) return { kind: "text", raw: original };
  const amb = parsed.ambiguous ? { ambiguous: true as const } : {};
  if (parsed.rest === "") return { kind: "number", value: parsed.value, ...amb };
  const unit = parsed.rest;
  if (unit.length <= KPI_UNIT_MAX_CHARS && UNIT_RE.test(unit)) {
    return { kind: "number", value: parsed.value, unit, ...amb };
  }
  return { kind: "text", raw: original };
}

/**
 * TR görüntü biçimi: binlik "." ondalık ",". `decimals` verilmezse tam sayı düz, kesirli en
 * fazla 2 hane (sondaki sıfırlar kırpılır); verilirse sabit hane. `percent` → "%41,3";
 * `unit` → "27.405,87 GJ". Sonlu olmayan değer "—".
 */
export function formatKpiNumber(
  value: number,
  opts?: {
    decimals?: number | undefined;
    unit?: string | undefined;
    percent?: boolean | undefined;
  },
): string {
  if (!Number.isFinite(value)) return "—";
  const fixed =
    opts?.decimals === undefined
      ? trimTrailingZeros(value.toFixed(2))
      : value.toFixed(Math.max(0, Math.min(20, Math.trunc(opts.decimals))));
  const negative = fixed.startsWith("-");
  const unsigned = negative ? fixed.slice(1) : fixed;
  const [intPart = "0", fracPart] = unsigned.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  let out = `${negative ? "-" : ""}${grouped}${fracPart !== undefined ? `,${fracPart}` : ""}`;
  if (opts?.percent) out = `%${out}`;
  if (opts?.unit) out = `${out} ${opts.unit}`;
  return out;
}

function trimTrailingZeros(fixed: string): string {
  if (!fixed.includes(".")) return fixed;
  const trimmed = fixed.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed === "-0" ? "0" : trimmed;
}
